import { useMutation } from '@tanstack/react-query';
import React, { useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  Button, Image, Input, Label, TextArea, Typography,
} from 'tfc-components';

import banner from 'assets/images/contactUs/form-image.png';
import Section from 'components/organisms/Section';
import CustomToastify from 'helpers/notify';
import { requestDemoService } from 'services/systems';
import { IRequestDemo } from 'services/systems/type';
import { returnValidateErrorMessages } from 'utils/errors';

const fieldToText = (field: string): string => {
  switch (field) {
    case 'name':
      return 'Full name';
    case 'email':
      return 'Email address';
    case 'phone':
      return 'Phone number';
    case 'companyName':
      return 'Company Name';
    case 'note':
      return 'Note';
    default:
      return field;
  }
};

const Form: React.FC = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    reset,
    setError
  } = useForm<IRequestDemo>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      companyName: '',
      note: '',
    },
  });
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const { mutate: submitContact, isPending: loading } = useMutation({
    mutationKey: ['contactForm'],
    mutationFn: requestDemoService,
    onSuccess: () => {
      toast(CustomToastify, {
        type: 'success',
        data: { message: 'Sending success' },
      });
    },
    onError: (error) => {
      if (Array.isArray(error)) {
        error.forEach((err) => {
          setError(err.field, {
            message: returnValidateErrorMessages(err, fieldToText(err.field)),
          });
        });
      } else {
        toast(CustomToastify, {
          type: 'error',
          data: { message: 'Sending failed' },
        });
      }
    },
  });

  const onSubmit = async (data: IRequestDemo) => {
    if (!recaptchaRef.current) return;
    const valid = await trigger();
    if (!valid) return;

    try {
      const token = await recaptchaRef.current.executeAsync();
      if (!token) return;
      submitContact({ ...data, ggRecaptchaToken: token });
    } catch (error) {
      toast(CustomToastify, {
        type: 'error',
        data: { message: 'Sending failed' },
      });
    } finally {
      recaptchaRef.current.reset();
      reset();
    }
  };

  return (
    <Section className="p-contact_form">
      <Section.Title className="">General Inquiries</Section.Title>
      <div className="p-contact_form_content">
        <Image
          imgSrc={banner}
          alt="contact-us-image"
          ratio={490 / 453}
          extendClasses="p-contact_form_banner"
        />

        <div className="p-contact_form_form">
          <Typography.Text
            fontweight="600"
            extendClasses="f-24x36 p-contact_form_title"
          >
            Let’s talk. For all questions, ideas, or introductions
          </Typography.Text>
          <Typography.Text extendClasses="p-contact_form_description">
            Let users send a message directly:
          </Typography.Text>

          <div className="p-contact_form_mt24">
            <Label>
              Name
              {' '}
              <span>*</span>
            </Label>
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Name is required' }}
              render={({ field }) => (
                <>
                  <Input
                    {...field}
                    extendClasses="fInput"
                    placeholder="Nguyễn Văn A"
                  />
                  {errors.name && (
                    <Typography.Text extendClasses="error f-12x16">
                      {errors.name.message}
                    </Typography.Text>
                  )}
                </>
              )}
            />
          </div>

          <div className="p-contact_form_groupInput">
            <div className="p-contact_form_mt24">
              <Label>
                Email
                {' '}
                <span>*</span>
              </Label>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email format',
                  },
                }}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      extendClasses="fInput"
                      placeholder="congty@email.com"
                    />
                    {errors.email && (
                      <Typography.Text extendClasses="error f-12x16">
                        {errors.email.message}
                      </Typography.Text>
                    )}
                  </>
                )}
              />
            </div>

            <div className="p-contact_form_mt24 p-contact_form_ml24">
              <Label>
                Phone
                {' '}
                <span>*</span>
              </Label>
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: 'Phone is required',
                  pattern: {
                    value: /^[0-9]+$/,
                    message: 'Phone must contain only numbers',
                  },
                }}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      extendClasses="fInput"
                      placeholder="Vietnam +84 "
                    />
                    {errors.phone && (
                      <Typography.Text extendClasses="error f-12x16">
                        {errors.phone.message}
                      </Typography.Text>
                    )}
                  </>
                )}
              />
            </div>
          </div>

          <div className="p-contact_form_mt24">
            <Label>Company</Label>
            <Controller
              name="companyName"
              control={control}
              render={({ field }) => (
                <>
                  <Input
                    {...field}
                    extendClasses="fInput"
                    placeholder="Company name"
                  />
                  {errors.companyName && (
                    <Typography.Text extendClasses="error f-12x16">
                      {errors.companyName.message}
                    </Typography.Text>
                  )}
                </>
              )}
            />
          </div>

          <div className="p-contact_form_mt24">
            <Label>Message</Label>
            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <>
                  <TextArea
                    {...field}
                    extendClasses="fInput p-contact_form_mutiple"
                    placeholder="Enter a description..."
                  />
                  {errors.note && (
                    <Typography.Text extendClasses="error f-12x16">
                      {errors.note.message}
                    </Typography.Text>
                  )}
                </>
              )}
            />
          </div>

          <Button
            extendClasses="fButton p-contact_form_button"
            onClick={handleSubmit(onSubmit)}
            loading={loading}
          >
            Send now
          </Button>
        </div>
      </div>
      <ReCAPTCHA ref={recaptchaRef} sitekey="6Lcp4MIZAAAAABHdGwULZhEv01tu2iODEDzSterA" size="invisible" />
    </Section>
  );
};

export default Form;
